import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import api from "../utils/api";
import "../styles/heathrowSurvey.css";

const featureOptions = [
  { code: "LEAVE_TIME", label: "Exact when-to-leave-home timing" },
  { code: "RAIL_ALERTS", label: "Rail disruption and change alerts" },
  { code: "ROAD_ALERTS", label: "Road and motorway congestion alerts" },
  { code: "WEATHER_ALERTS", label: "Weather warnings that affect the trip" },
  { code: "LIVE_FARES", label: "Live rail, taxi and parking costs" },
  { code: "TERMINAL_ROUTE", label: "Best route and exact terminal reach time" },
  { code: "FLIGHT_ALERTS", label: "Flight delay, cancellation and gate alerts" },
  { code: "QUEUE_ALERTS", label: "Security and immigration queue updates" }
];

const timingOptions = [
  { code: "DAY_BEFORE", label: "From 1 day before travel" },
  { code: "MORNING_OF", label: "Morning of travel" },
  { code: "THREE_TO_FOUR_HOURS", label: "3 to 4 hours before flight" },
  { code: "ONLY_IF_ISSUES", label: "Only when something goes wrong" }
];

const accessOptions = [
  { code: "SCAN_QR", label: "Scan a QR code on the day" },
  { code: "WEB_LINK", label: "Open it from a web link before travel" },
  { code: "EMAIL_ALERTS", label: "Email alerts before the flight" },
  { code: "SMS_ALERTS", label: "SMS or WhatsApp style alerts" }
];

const paymentOptions = [
  { code: "FREE_ONLY", label: "Only if it is free" },
  { code: "MAYBE_SMALL_FEE", label: "Maybe for a small one-off fee" },
  { code: "YES_IF_VALUABLE", label: "Yes, if it clearly saves stress or a missed flight" },
  { code: "NO_NEED", label: "No, I would not pay for this" }
];

const helpfulnessOptions = [
  { code: "VERY_HELPFUL", label: "Very helpful" },
  { code: "SOMEWHAT_HELPFUL", label: "Somewhat helpful" },
  { code: "NOT_SURE", label: "Not sure yet" },
  { code: "NOT_HELPFUL", label: "Not helpful for me" }
];

const useOptions = [
  { code: "YES", label: "Yes" },
  { code: "MAYBE", label: "Maybe" },
  { code: "NO", label: "No" }
];

const AJPSurvey = () => {
  const [wouldUse, setWouldUse] = useState("");
  const [topFeatures, setTopFeatures] = useState([]);
  const [alertTiming, setAlertTiming] = useState("");
  const [helpfulness, setHelpfulness] = useState("");
  const [wouldPay, setWouldPay] = useState("");
  const [accessPreference, setAccessPreference] = useState([]);
  const [liveCostUseful, setLiveCostUseful] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comments, setComments] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const pagePath = "/survey";
  const pageTitle = "Airport Journey Planner Survey";
  const plannerSurveyUrl = "https://www.airportjourneyplanner.com/survey";
  const qrSrc = `${String(api.defaults.baseURL || "").replace(/\/$/, "")}/api/qr?target=${encodeURIComponent(plannerSurveyUrl)}&size=600`;

  const toggleArrayValue = (arr, value) =>
    arr.includes(value) ? arr.filter(item => item !== value) : [...arr, value];

  const fetchToken = async () => {
    const res = await api.post("/api/feedback-token", {
      page_path: pagePath,
      page_title: pageTitle
    });
    setToken(res.data?.token || "");
    return res.data?.token || "";
  };

  useEffect(() => {
    fetchToken().catch(() => {
      setToken("");
    });
  }, []);

  const summaryMessage = useMemo(() => {
    const lines = [
      `AJP market survey response`,
      `Would use: ${wouldUse || "No answer"}`,
      `Top features: ${topFeatures.length ? topFeatures.join(", ") : "No answer"}`,
      `Alert timing: ${alertTiming || "No answer"}`,
      `Expected helpfulness: ${helpfulness || "No answer"}`,
      `Would pay: ${wouldPay || "No answer"}`,
      `Live cost comparison useful: ${liveCostUseful || "No answer"}`,
      `Access preference: ${accessPreference.length ? accessPreference.join(", ") : "No answer"}`,
      `Comments: ${comments.trim() || "None"}`
    ];
    return lines.join("\n");
  }, [
    accessPreference,
    alertTiming,
    comments,
    helpfulness,
    liveCostUseful,
    topFeatures,
    wouldPay,
    wouldUse
  ]);

  const surveyPayload = {
    survey_type: "AJP_MARKET_VALIDATION",
    would_use: wouldUse || null,
    top_features: topFeatures,
    alert_timing: alertTiming || null,
    helpfulness: helpfulness || null,
    would_pay: wouldPay || null,
    live_cost_useful: liveCostUseful || null,
    access_preference: accessPreference,
    name: name.trim() || null,
    email: email.trim() || null,
    source: "ON_SITE",
    consent: true,
    additional_comments: comments.trim() || null
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email or leave it blank.");
      return;
    }

    try {
      setLoading(true);
      try {
        await api.post("/api/surveys/ajp", surveyPayload);
      } catch (surveyErr) {
        const status = surveyErr?.response?.status;
        if (status && status !== 404) throw surveyErr;

        const submitToken = token || (await fetchToken());
        await api.post("/api/feedback", {
          token: submitToken,
          name: name.trim() || null,
          email: email.trim() || null,
          liked: wouldUse === "YES" ? true : wouldUse === "NO" ? false : null,
          message: summaryMessage,
          honeypot,
          page_path: pagePath,
          page_url: window.location.href,
          page_title: pageTitle
        });
      }

      setSuccess(true);
      setWouldUse("");
      setTopFeatures([]);
      setAlertTiming("");
      setHelpfulness("");
      setWouldPay("");
      setAccessPreference([]);
      setLiveCostUseful("");
      setName("");
      setEmail("");
      setComments("");
      setHoneypot("");
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "We could not submit the survey right now.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tool-page">
      <Helmet>
        <title>Airport Journey Planner Survey | Airport Journey Planner</title>
        <meta
          name="description"
          content="Quick survey to see whether airport passengers would use Airport Journey Planner for live alerts, route timing and disruption updates."
        />
        <link rel="canonical" href="https://www.airportjourneyplanner.com/survey" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Airport Journey Planner Survey" />
        <meta
          property="og:description"
          content="Tell us whether live airport journey alerts, rail disruption warnings and exact leave-home timing would help you."
        />
        <meta property="og:url" content="https://www.airportjourneyplanner.com/survey" />
      </Helmet>

      <main className="tool-container">
        <header className="tool-header">
          <h1>Airport Journey Planner Survey</h1>
          <p className="tool-intro">
            We will show you the tool output first, then ask a few quick questions.
            <br />
            All answers are optional. This survey is only to test whether passengers want and value this service.
          </p>
        </header>

        <section className="tool-grid">
          <article className="tool-card">
            <div
              style={{
                background: "#f8fbff",
                border: "1px solid #dce9fb",
                borderRadius: 14,
                padding: 14,
                marginBottom: 16
              }}
            >
              <strong>Why this matters</strong>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "#4b5563", lineHeight: 1.55 }}>
                Many airport journeys go wrong before check-in: late rail changes, motorway congestion,
                weather disruption or long security queues. We want to know whether earlier alerts and
                exact timing guidance would actually help passengers.
              </p>
            </div>

            <form className="tool-form" onSubmit={handleSubmit}>
              <input
                type="text"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                style={{ display: "none" }}
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="survey-block">
                <h3>1. If this tool worked well, would you use it for an airport trip?</h3>
                <div className="survey-rating">
                  {useOptions.map(option => (
                    <button
                      type="button"
                      key={option.code}
                      className={`survey-chip ${wouldUse === option.code ? "active" : ""}`}
                      onClick={() => setWouldUse(option.code)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>2. Which parts would be most useful to you? Choose any.</h3>
                <div className="survey-options">
                  {featureOptions.map(option => (
                    <label key={option.code} className="survey-check">
                      <input
                        type="checkbox"
                        checked={topFeatures.includes(option.code)}
                        onChange={() => setTopFeatures(toggleArrayValue(topFeatures, option.code))}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>3. When would you want alerts the most?</h3>
                <div className="survey-rating">
                  {timingOptions.map(option => (
                    <button
                      type="button"
                      key={option.code}
                      className={`survey-chip ${alertTiming === option.code ? "active" : ""}`}
                      onClick={() => setAlertTiming(option.code)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>4. Do you think this would genuinely help your airport journey?</h3>
                <div className="survey-rating">
                  {helpfulnessOptions.map(option => (
                    <button
                      type="button"
                      key={option.code}
                      className={`survey-chip ${helpfulness === option.code ? "active" : ""}`}
                      onClick={() => setHelpfulness(option.code)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>5. Would live rail, road, taxi and parking costs help you choose how to travel?</h3>
                <div className="survey-rating">
                  {useOptions.map(option => (
                    <button
                      type="button"
                      key={option.code}
                      className={`survey-chip ${liveCostUseful === option.code ? "active" : ""}`}
                      onClick={() => setLiveCostUseful(option.code)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>6. Would you pay for this if it clearly reduced stress or helped avoid a missed flight?</h3>
                <div className="survey-options">
                  {paymentOptions.map(option => (
                    <label key={option.code} className="survey-check">
                      <input
                        type="radio"
                        name="wouldPay"
                        checked={wouldPay === option.code}
                        onChange={() => setWouldPay(option.code)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>7. How would you most likely access it?</h3>
                <div className="survey-options">
                  {accessOptions.map(option => (
                    <label key={option.code} className="survey-check">
                      <input
                        type="checkbox"
                        checked={accessPreference.includes(option.code)}
                        onChange={() => setAccessPreference(toggleArrayValue(accessPreference, option.code))}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-two-col" style={{ marginTop: 16 }}>
                <div className="tool-form-row">
                  <label>Name (optional)</label>
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>

                <div className="tool-form-row">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="survey-block" style={{ marginTop: 16 }}>
                <h3>Anything else you would want this tool to do?</h3>
                <div className="tool-form-row" style={{ marginTop: 8 }}>
                  <textarea
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Extra thoughts on alerts, route planning, queues, price comparison or whether you would trust this."
                    rows={4}
                    style={{ resize: "vertical" }}
                    maxLength={700}
                  />
                </div>
              </div>

              {error && <div className="tool-error" style={{ marginTop: 12 }}>{error}</div>}
              {success && (
                <div className="survey-success" style={{ marginTop: 12 }}>
                  <strong>Thank you. Your response has been recorded.</strong>
                </div>
              )}

              <div className="survey-actions">
                <Link className="secondary-btn" to="/privacy">
                  Privacy
                </Link>
                <button type="submit" className="primary-btn" disabled={loading}>
                  {loading ? "Submitting..." : "Submit Survey"}
                </button>
              </div>
            </form>
          </article>

          <article className="tool-card tool-results-card">
            <h2>Quick field use</h2>
            <p style={{ fontSize: 14 }}>
              Show the passenger the planner output first, then ask whether they would use it,
              which alerts matter most, and whether they would pay for it.
            </p>

            <div className="survey-mini-links">
              <strong>Best talking points:</strong>
              <ul>
                <li>When to leave home based on live conditions</li>
                <li>Rail and road disruption alerts before the journey breaks down</li>
                <li>Live cost comparison across rail, taxi and parking</li>
                <li>Flight, queue, terminal and gate updates in one place</li>
              </ul>
            </div>

            <div className="survey-block" style={{ textAlign: "center", marginTop: 16 }}>
              <h3 style={{ marginBottom: 6 }}>Scan to open the survey</h3>
              <img
                src={qrSrc}
                alt="Airport Journey Planner survey QR code"
                style={{ width: 220, maxWidth: "70%", borderRadius: 12 }}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                airportjourneyplanner.com/survey
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 14,
                background: "#0f172a",
                color: "#fff"
              }}
            >
              <strong>Suggested manual fallback</strong>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "#dbeafe", lineHeight: 1.6 }}>
                If internet or QR is slow on the day, the same questions can be asked manually and
                entered later by staff.
              </p>
            </div>
          </article>
        </section>

        <section className="tool-section" style={{ marginTop: 32 }}>
          <h2>Customer Reviews</h2>
          <ReviewsBar />
        </section>
      </main>
    </div>
  );
};

export default AJPSurvey;
